import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  SplitButton,
  FluentProvider,
  webLightTheme,
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverSurface,
  TeachingPopoverTitle,
  TeachingPopoverFooter,
  makeStyles,
} from '@fluentui/react-components';
import { DismissRegular, SaveRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  stackMargin: { margin: '10px 0' },
});

export interface TeachingPopoverProps {
  showTeachingPopover?: boolean;
  header?: string;
  content?: string;
  setShowTeachingPopover?: (show: boolean) => void;
}

interface IActionButtonsProps {
  mutationLoading?: boolean;
  closePanel?: () => void;
  handleSubmit: () => void;
  saveTitle: string;
  saveLabel: string;
  cancelTitle: string;
  cancelLabel: string;
  className?: string;
  split?: boolean;
  menuItems?: Array<{ key: string; text: string; onClick: () => void }>;
  disabled?: boolean;
  teachingPopoverProps?: TeachingPopoverProps;
}

const ActionButtons = (props: IActionButtonsProps): JSX.Element => {
  const {
    mutationLoading,
    closePanel,
    handleSubmit,
    className,
    saveTitle = 'Save',
    saveLabel = 'Save',
    cancelTitle = 'Cancel',
    cancelLabel = 'Cancel',
    split = false,
    menuItems,
    disabled,
    teachingPopoverProps,
  } = props;

  const { showTeachingPopover, header, content, setShowTeachingPopover } =
    teachingPopoverProps || {};
  const classes = useStyles();

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={className}>
        <Stack
          wrap
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 20 }}
          className={classes.stackMargin}
        >
          {mutationLoading && <Spinner aria-label="Loading" />}
          <Button
            icon={<DismissRegular />}
            appearance="transparent"
            title={cancelTitle}
            aria-label={cancelLabel}
            onClick={closePanel}
            disabled={mutationLoading}
          >
            Cancel
          </Button>
          <TeachingPopover
            open={showTeachingPopover}
            onOpenChange={(e, data) => {
              if (!data.open && setShowTeachingPopover) {
                setShowTeachingPopover(false);
              }
            }}
          >
            {split && menuItems ? (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <SplitButton
                    id="primaryButtonBubbleHook"
                    icon={<SaveRegular />}
                    appearance="primary"
                    primaryActionButton={{
                      onClick: handleSubmit,
                    }}
                    disabled={mutationLoading || disabled}
                    menuButton={{
                      'aria-label': 'See 2 options',
                    }}
                  >
                    {saveLabel}
                  </SplitButton>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {menuItems?.map((item) => (
                      <MenuItem key={item.key || item.text} onClick={item.onClick}>
                        {item.text}
                      </MenuItem>
                    ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            ) : (
              <Button
                id="primaryButtonBubbleHook"
                icon={<SaveRegular />}
                appearance="primary"
                title={saveTitle}
                aria-label={saveLabel}
                onClick={handleSubmit}
                disabled={mutationLoading || disabled}
              >
                {saveLabel}
              </Button>
            )}
            <TeachingPopoverSurface>
              <TeachingPopoverBody>
                <TeachingPopoverTitle>{header}</TeachingPopoverTitle>
                <div>{content}</div>
              </TeachingPopoverBody>
              <TeachingPopoverFooter primary="Close" />
            </TeachingPopoverSurface>
          </TeachingPopover>
        </Stack>
      </div>
    </FluentProvider>
  );
};

export default ActionButtons;
